import axios from 'axios';
import { Auth } from '../routes/login/auth';
import { Integrations, PushAccessInformation } from './types';

export const checkAccessInfo = async (
  api: string,
  businessUnitId: string,
  subdomain: string,
  metadataIntegrationSecret: string
): Promise<{ hasAccess: boolean }> => {
  const headers = {
    'Content-Type': 'application/json',
    'authorization': `Bearer ${Auth.getAccessToken()}`,
  };

  try {
    return (await axios
      .get(
        `${api}/v1/private/zendesk/business-units/${businessUnitId}/${subdomain}/check/${metadataIntegrationSecret}`,
        { headers }
      )).data;
  } catch (err) {
    if (err.response) {
      Auth.resetTokenIfUnauthorized(err.response);
    }
    return {
      hasAccess: false,
    };
  }
};

export const getIntegrations = async (
  api: string,
  businessUserId: string,
  subdomain: string,
  integrationSecret?: string,
): Promise<Integrations | undefined> => {
  const headers = {
    'Content-Type': 'application/json',
    'authorization': `Bearer ${Auth.getAccessToken()}`,
  };

  const body = {
    integrationSecret
  }; 

  try {
    return (await axios
      .post(
        `${api}/v1/private/zendesk/business-units/${businessUserId}/get/${subdomain}`,
        body,
        { headers }
      )).data;
  } catch (err) {
    if (err.response) {
      Auth.resetTokenIfUnauthorized(err.response);
    }
    return undefined;
  }
};

export const saveIntegrations = async (
  api: string,
  businessUserId: string,
  subdomain: string,
  businessUnits: { businessUnitId: string, enabled: boolean }[],
  stars: number[],
  preTags: boolean,
  productReviews: boolean,
  pushAccessInformation?: PushAccessInformation,
  integrationSecret?: string,
): Promise<Integrations | undefined> => {
  const headers = {
    'Content-Type': 'application/json',
    'authorization': `Bearer ${Auth.getAccessToken()}`,
  };
  const body = {
    businessUnits,
    stars,
    preTags,
    productReviews,
    pushAccessInformation,
    integrationSecret,
  };

  try {
    await axios.post(
      `${api}/v1/private/zendesk/business-units/${businessUserId}/${subdomain}`,
      body,
      { headers }
    );
  } catch (err) {
    if (err.response) {
      Auth.resetTokenIfUnauthorized(err.response);
    }
    return undefined;
  }
};

export const getOAuthLink = async (
  api: string
): Promise<Integrations | undefined> => {
  const headers = {
    'Content-Type': 'application/json',
  };

  try {
    return (await axios.get(`${api}`, { headers })).data.oauthUrl;
  } catch (err) {
    return undefined;
  }
};

export const getDoesExist = async (api: string, subdomain: string): Promise<boolean> => {
  const headers = { 'authorization': `Bearer ${Auth.getAccessToken()}` };

  try {
  return (await axios
    .get(
      `${api}/v1/private/zendesk/${subdomain}`,
      { headers }
    )).data.exist;
  } catch (err) {
    if (err.response) {
      Auth.resetTokenIfUnauthorized(err.response);
    }
    return false;
  }
};

export const syncReviews = async (api: string, syncPeriod: string, businessUnitIds: string[], subdomain: string): Promise<void> => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      'authorization': `Bearer ${Auth.getAccessToken()}`,
    };

    await Promise.all(
      businessUnitIds.map(async (businessUnitId) => {
        return await axios.post(
          `${api}/v1/private/zendesk/business-units/sync/${businessUnitId}/${subdomain}/${syncPeriod}`,
          {},
          { headers },
        );
      })
    );
  } catch (err) {
    if (err.response) {
      Auth.resetTokenIfUnauthorized(err.response);
    }
  }
};
